/** @sfdc-extension-file SNK */
'use client';

import { Suspense, useMemo, type ComponentProps } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { Region, type ComponentType } from '@/components/region';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { LinkColumn } from '@/shared-ui/molecules/link-column';
import { cn } from '@/lib/utils';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';

export const LINK_COLUMN_ITEMS_REGION_ID = 'items';

const linkColumnDefaults = {
    heading: 'Help & Information',
} as const;

interface LinkColumnDesignerProps extends ComponentProps<'div'> {
    /** Column heading rendered by `LinkColumn.ListColumn`. */
    heading?: string;

    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('linkColumn', {
    name: 'Link Column',
    description:
        'Vertical list of Link Item components with a heading, built on `@/shared-ui/molecules/link-column` (`variant="list"`).',
})
@RegionDefinition([
    {
        id: LINK_COLUMN_ITEMS_REGION_ID,
        name: 'Link items',
        description: 'Add Menu Item components in the order they should appear in the column.',
        componentTypeInclusions: ['menuItem'],
    },
])
export class LinkColumnMetadata {
    @AttributeDefinition({
        id: 'heading',
        name: 'Heading',
        description: 'Heading text shown above the link list.',
        type: 'string',
        required: true,
        defaultValue: linkColumnDefaults.heading,
    })
    heading?: string;
}
/* v8 ignore stop */

export default function LinkColumnDesigner({
    className,
    heading = linkColumnDefaults.heading,
    regionId: _regionId,
    component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    ...props
}: LinkColumnDesignerProps) {
    const componentWithRegionMetadata = useMemo(
        () => (component ? withComponentRegionDesignMetadata(component, LinkColumnMetadata) : null),
        [component]
    );

    if (!component || !componentWithRegionMetadata) {
        return null;
    }

    const itemsRegion = component.regions?.find((region) => region.id === LINK_COLUMN_ITEMS_REGION_ID);

    if (!itemsRegion) {
        return null;
    }

    const hasItems = (itemsRegion.components?.length ?? 0) > 0;

    return (
        <div className={cn('w-full px-4 py-4', className)} {...props}>
            {hasItems ? (
                <LinkColumn.ListColumn title={heading}>
                    <Suspense fallback={null}>
                        <Region component={componentWithRegionMetadata} regionId={itemsRegion.id} errorElement={null} />
                    </Suspense>
                </LinkColumn.ListColumn>
            ) : (
                <Suspense fallback={null}>
                    <Region component={componentWithRegionMetadata} regionId={itemsRegion.id} errorElement={null} />
                </Suspense>
            )}
        </div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export { LinkColumnPdSkeleton, LinkColumnPdSkeleton as fallback } from './skeleton';
